import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Loader2, Clock, CreditCard, CheckCircle2, XCircle,
  AlertTriangle, MessageCircle, Phone, Calendar, Users, Receipt,
  Instagram, Facebook, Globe,
} from "lucide-react";
import { FormPagoReserva } from "./FormPagoReserva";
import { aplicarFaviconPagina, aplicarTituloPagina } from "../../lib/branding";

interface UltimoPago {
  id: number;
  estado: "pendiente" | "verificado" | "rechazado" | "anulado";
  montoUsd: number;
  moneda?: string;
  metodo?: string;
  banco?: string;
  numeroReferencia?: string;
  fechaPago?: string;
  createdAt: string;
  motivoRechazo?: string;
  comprobanteUrl?: string;
  origen?: string;
}

interface StatusData {
  id: number;
  estado: string; // pendiente, confirmada, cancelada, ...
  fechaReserva: string;
  horaReserva: string;
  numPersonas: number;
  nombreCliente: string;
  zonaNombre?: string | null;
  sucursalId?: number | null;
  sucursalNombre?: string | null;
  sucursalTelefono?: string | null;
  sucursalWhatsapp?: string | null;
  requierePago: boolean;
  montoReservaUsd: number | null;
  instruccionesPago: string | null;
  horasLimite: number;
  expiraEn: string | null;
  minutosRestantes: number | null;
  ultimoPago: UltimoPago | null;
}

function fmtFecha(s: string) {
  try {
    const d = new Date(s + "T00:00:00");
    return d.toLocaleDateString("es-VE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return s; }
}
function fmtHora(s: string) { return (s ?? "").slice(0, 5); }
function fmtMin(mins: number) {
  if (mins <= 0) return "Expirado";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

interface MenuConfigBranding {
  logoUrl?: string | null;
  imagenFondoUrl?: string | null;
  colorPrimario?: string | null;
}

export default function ReservaStatusPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<StatusData | null>(null);
  const [cargando, setCargando] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  const [ahora, setAhora] = useState(Date.now());
  const [menuConfig, setMenuConfig] = useState<MenuConfigBranding | null>(null);

  // Polling suave cada 30s + tick cada segundo para countdown
  useEffect(() => {
    let cancel = false;
    setCargando(true);
    fetch(`/api/v1/reservas/${id}/status`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error("No se pudo cargar la reserva.")))
      .then((d: StatusData) => { if (!cancel) setData(d); })
      .catch((e: Error) => { if (!cancel) setErr(e.message); })
      .finally(() => { if (!cancel) setCargando(false); });
    return () => { cancel = true; };
  }, [id, recarga]);

  useEffect(() => {
    const t = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Identidad visual de la sucursal donde se hizo la reserva — mismo fondo,
  // logo, favicon y título de pestaña que /reservar, para que "Ver mi
  // reserva" (botón y link del correo) se sienta la misma página, no una
  // genérica.
  useEffect(() => {
    if (!data?.sucursalId) return;
    fetch(`/api/v1/menu-publico/sucursal/${data.sucursalId}/config`)
      .then((r) => r.json())
      .then((d) => setMenuConfig({ logoUrl: d.logoUrl, imagenFondoUrl: d.imagenFondoUrl, colorPrimario: d.colorPrimario }))
      .catch(() => {});
  }, [data?.sucursalId]);

  useEffect(() => {
    aplicarFaviconPagina(menuConfig?.logoUrl);
    aplicarTituloPagina(data?.sucursalNombre);
  }, [menuConfig?.logoUrl, data?.sucursalNombre]);

  // Redes sociales de la sucursal (para mostrarlas en el pie)
  const [redes, setRedes] = useState<{ instagramUrl?: string | null; facebookUrl?: string | null; tripadvisorUrl?: string | null; websiteUrl?: string | null } | null>(null);
  useEffect(() => {
    if (!data?.sucursalId) return;
    fetch(`/api/v1/sucursales/${data.sucursalId}/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setRedes(d); })
      .catch(() => {});
  }, [data?.sucursalId]);

  // Confirmar / cancelar la reserva desde la web
  const [accionando, setAccionando] = useState(false);
  const confirmarReserva = async () => {
    setAccionando(true);
    try { await fetch(`/api/v1/reservas/${id}/confirmar-publico`, { method: "POST" }); setRecarga((r) => r + 1); }
    finally { setAccionando(false); }
  };
  const cancelarReserva = async () => {
    if (!window.confirm("¿Querés cancelar tu reserva?")) return;
    setAccionando(true);
    try { await fetch(`/api/v1/reservas/${id}/cancelar-publico`, { method: "POST" }); setRecarga((r) => r + 1); }
    finally { setAccionando(false); }
  };

  // Tiempo restante en vivo (recalculado cada segundo)
  const minutosRest = useMemo(() => {
    if (!data?.expiraEn) return null;
    const diff = Math.round((new Date(data.expiraEn).getTime() - ahora) / 60000);
    return Math.max(0, diff);
  }, [data?.expiraEn, ahora]);

  if (cargando) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="size-7 animate-spin text-stone-400" />
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <div className="bg-white rounded-2xl border border-stone-200 p-8 max-w-md text-center">
          <XCircle className="size-12 text-rose-500 mx-auto mb-3" aria-hidden />
          <h1 className="font-bold text-lg text-stone-800">Reserva no encontrada</h1>
          <p className="text-sm text-stone-500 mt-2">
            {err ?? "No pudimos encontrar la reserva. Revisá el enlace."}
          </p>
          <Link to="/reservar" className="inline-block mt-4 px-4 py-2 bg-stone-900 text-white rounded-lg text-sm">
            Hacer una reserva
          </Link>
        </div>
      </div>
    );
  }

  const p = data.ultimoPago;
  const cancelada = data.estado === "cancelada" || data.estado === "auto_cancelada";
  const confirmada = data.estado === "confirmada" || data.estado === "atendida";
  const verificado = p?.estado === "verificado";
  const expirando = minutosRest !== null && minutosRest > 0 && minutosRest < 60;
  const expirado = minutosRest === 0 && !verificado;

  // ¿Debemos mostrar el formulario para reportar pago?
  const puedeReportarPago =
    data.requierePago &&
    !confirmada &&
    !cancelada &&
    (!p || p.estado === "rechazado");

  // Status badge
  const badge = cancelada
    ? { cls: "bg-rose-100 text-rose-700 border-rose-200", txt: "Cancelada", Icon: XCircle }
    : confirmada
      ? { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", txt: "Confirmada", Icon: CheckCircle2 }
      : verificado
        ? { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", txt: "Pago verificado", Icon: CheckCircle2 }
        : p?.estado === "pendiente"
          ? { cls: "bg-amber-100 text-amber-700 border-amber-200", txt: "Esperando verificación", Icon: Clock }
          : data.requierePago
            ? { cls: "bg-amber-100 text-amber-700 border-amber-200", txt: "Pendiente de pago", Icon: AlertTriangle }
            : { cls: "bg-sky-100 text-sky-700 border-sky-200", txt: "Pendiente de confirmación", Icon: Clock };
  const BadgeIcon = badge.Icon;

  const accent = menuConfig?.colorPrimario ?? "#6E6354";

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-stone-50 via-stone-50 to-white"
      style={menuConfig?.imagenFondoUrl ? {
        backgroundImage: `linear-gradient(rgba(246,241,231,0.88), rgba(246,241,231,0.88)), url(${menuConfig.imagenFondoUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      } : undefined}
    >
      <div className="max-w-xl mx-auto px-4 py-8 sm:py-12">
        <div className="rounded-3xl border-2 bg-white/95 shadow-xl backdrop-blur-sm px-5 py-7 sm:px-8 sm:py-9 text-center" style={{ borderColor: accent }}>

        <header className="mb-5">
          {menuConfig?.logoUrl && (
            <img
              src={menuConfig.logoUrl}
              alt={data.sucursalNombre ?? "Logo"}
              style={{ height: 64, width: 64, borderRadius: "50%", objectFit: "cover", margin: "0 auto 14px", boxShadow: "0 4px 18px rgba(0,0,0,0.12)", border: `3px solid ${accent}` }}
            />
          )}
          <p className="text-[11px] uppercase tracking-widest text-stone-400 font-semibold mb-1">
            {data.sucursalNombre}
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">
            Reserva #{data.id}
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            Hola {data.nombreCliente.split(/\s+/)[0]}, este es el estado de tu reserva.
          </p>
        </header>

        {/* Status badge prominente */}
        <div className={`rounded-2xl border-2 p-4 mb-4 inline-flex items-center gap-3 ${badge.cls}`}>
          <BadgeIcon size={22} aria-hidden />
          <div>
            <p className="font-bold text-base leading-tight">{badge.txt}</p>
            {p?.estado === "rechazado" && p.motivoRechazo && (
              <p className="text-xs mt-0.5">Motivo: {p.motivoRechazo}</p>
            )}
          </div>
        </div>

        {/* Datos de la reserva */}
        <section className="bg-white rounded-2xl border border-stone-200 p-5 mb-4">
          <h2 className="text-sm font-bold text-stone-800 mb-3">Datos de tu reserva</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <Field icon={<Calendar size={16} />} label="Fecha" value={fmtFecha(data.fechaReserva)} />
            <Field icon={<Clock size={16} />} label="Hora" value={fmtHora(data.horaReserva)} />
            <Field icon={<Users size={16} />} label="Personas" value={String(data.numPersonas)} />
            {data.zonaNombre && <Field icon={<Receipt size={16} />} label="Zona" value={data.zonaNombre} />}
          </dl>
        </section>

        {/* Acciones del cliente: confirmar / cancelar */}
        {!cancelada && !confirmada && (
          <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
            {!data.requierePago && (
              <button onClick={confirmarReserva} disabled={accionando}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold text-sm shadow disabled:opacity-60"
                style={{ background: accent }}>
                <CheckCircle2 size={16} aria-hidden /> Confirmar reserva
              </button>
            )}
            <button onClick={cancelarReserva} disabled={accionando}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 font-semibold text-sm bg-white disabled:opacity-60"
              style={{ borderColor: accent, color: accent }}>
              <XCircle size={16} aria-hidden /> Cancelar reserva
            </button>
          </div>
        )}

        {/* Countdown — solo si requiere pago, está pendiente y no expiró */}
        {puedeReportarPago && minutosRest !== null && (
          <section className={`rounded-2xl border-2 p-4 mb-4 ${
            expirado ? "border-rose-300 bg-rose-50" :
            expirando ? "border-amber-300 bg-amber-50" :
            "border-emerald-200 bg-emerald-50/60"
          }`}>
            <div className="flex items-start gap-3">
              <div className={`shrink-0 size-12 rounded-xl inline-flex items-center justify-center ${
                expirado ? "bg-rose-200 text-rose-700" :
                expirando ? "bg-amber-200 text-amber-700" :
                "bg-emerald-200 text-emerald-700"
              }`}>
                <Clock size={22} aria-hidden />
              </div>
              <div className="flex-1">
                <p className={`text-sm font-bold ${
                  expirado ? "text-rose-700" : expirando ? "text-amber-800" : "text-emerald-800"
                }`}>
                  {expirado
                    ? "El plazo para pagar expiró"
                    : `Tenés ${fmtMin(minutosRest)} para confirmar el pago`}
                </p>
                <p className="text-xs text-stone-600 mt-0.5 leading-relaxed">
                  {expirado
                    ? "Esta reserva será cancelada automáticamente. Si ya pagaste, contactanos al "
                    : `Después de las ${data.horasLimite} horas la reserva se cancela automáticamente. `}
                  {expirado && data.sucursalWhatsapp && (
                    <a href={`https://wa.me/${data.sucursalWhatsapp.replace(/\D/g, "")}`}
                      target="_blank" rel="noreferrer"
                      className="text-emerald-700 underline font-semibold">
                      WhatsApp del local
                    </a>
                  )}
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Pago reportado (si existe) */}
        {p && (
          <section className="bg-white rounded-2xl border border-stone-200 p-5 mb-4">
            <h2 className="text-sm font-bold text-stone-800 mb-3 flex items-center gap-2">
              <CreditCard size={16} className="text-emerald-600" aria-hidden />
              Pago reportado
            </h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <Field label="Monto" value={`${p.moneda ?? 'USD'} ${p.montoUsd.toFixed(2)}`} />
              {p.metodo && <Field label="Método" value={p.metodo} />}
              {p.banco && <Field label="Banco" value={p.banco} />}
              {p.numeroReferencia && <Field label="Referencia" value={p.numeroReferencia} />}
              {p.fechaPago && <Field label="Fecha del pago" value={p.fechaPago} />}
            </dl>
            {p.comprobanteUrl && (
              <div className="mt-3">
                {p.comprobanteUrl.match(/\.(jpg|jpeg|png|webp|gif)$/i) ? (
                  <a href={p.comprobanteUrl} target="_blank" rel="noopener">
                    <img src={p.comprobanteUrl} alt="Comprobante"
                      style={{ maxHeight: 160, borderRadius: 12, border: "1px solid #e5e7eb", objectFit: "contain" }} />
                  </a>
                ) : (
                  <a href={p.comprobanteUrl} target="_blank" rel="noopener"
                    className="text-xs text-emerald-700 underline">
                    Ver comprobante adjunto
                  </a>
                )}
              </div>
            )}
            {p.estado === "pendiente" && (
              <p className="text-xs text-amber-700 mt-3 flex items-center gap-1.5">
                <Clock size={12} aria-hidden /> Estamos verificando tu pago, te confirmamos pronto.
              </p>
            )}
            {p.estado === "verificado" && (
              <p className="text-xs text-emerald-700 mt-3 flex items-center gap-1.5">
                <CheckCircle2 size={12} aria-hidden /> Pago verificado, tu reserva está confirmada.
              </p>
            )}
          </section>
        )}

        {/* Formulario de pago (si corresponde) */}
        {puedeReportarPago && !expirado && (
          <section className="mb-4">
            <FormPagoReserva
              reservaId={data.id}
              montoSugerido={data.montoReservaUsd}
              instrucciones={data.instruccionesPago}
              defaults={{ nombre: data.nombreCliente }}
              onReportado={() => setRecarga(r => r + 1)}
            />
          </section>
        )}

        {/* Footer de contacto */}
        {(data.sucursalTelefono || data.sucursalWhatsapp) && (
          <footer className="text-center mt-6">
            <p className="text-xs text-stone-500 mb-2">¿Dudas? Contactanos:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {data.sucursalTelefono && (
                <a href={`tel:${data.sucursalTelefono}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-stone-200 rounded-lg text-sm hover:bg-stone-50">
                  <Phone size={14} aria-hidden /> {data.sucursalTelefono}
                </a>
              )}
              {data.sucursalWhatsapp && (
                <a href={`https://wa.me/${data.sucursalWhatsapp.replace(/\D/g, "")}?text=Hola, consulta sobre la reserva %23${data.id}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm font-medium hover:bg-green-100">
                  <MessageCircle size={14} aria-hidden /> WhatsApp
                </a>
              )}
            </div>
          </footer>
        )}

        {/* Redes sociales de la sucursal */}
        {redes && (redes.instagramUrl || redes.facebookUrl || redes.websiteUrl || redes.tripadvisorUrl) && (
          <div className="mt-5 flex items-center justify-center gap-4">
            {redes.instagramUrl && (
              <a href={redes.instagramUrl} target="_blank" rel="noreferrer" aria-label="Instagram"
                className="text-stone-400 transition-colors" style={{ color: undefined }}
                onMouseOver={(e) => (e.currentTarget.style.color = accent)}
                onMouseOut={(e) => (e.currentTarget.style.color = "")}>
                <Instagram size={20} aria-hidden />
              </a>
            )}
            {redes.facebookUrl && (
              <a href={redes.facebookUrl} target="_blank" rel="noreferrer" aria-label="Facebook"
                className="text-stone-400 transition-colors"
                onMouseOver={(e) => (e.currentTarget.style.color = accent)}
                onMouseOut={(e) => (e.currentTarget.style.color = "")}>
                <Facebook size={20} aria-hidden />
              </a>
            )}
            {redes.websiteUrl && (
              <a href={redes.websiteUrl} target="_blank" rel="noreferrer" aria-label="Sitio web"
                className="text-stone-400 transition-colors"
                onMouseOver={(e) => (e.currentTarget.style.color = accent)}
                onMouseOut={(e) => (e.currentTarget.style.color = "")}>
                <Globe size={20} aria-hidden />
              </a>
            )}
          </div>
        )}

        </div>
      </div>
    </div>
  );
}

function Field({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-stone-400 font-semibold mb-0.5 flex items-center gap-1">
        {icon} {label}
      </dt>
      <dd className="text-stone-800 font-medium">{value}</dd>
    </div>
  );
}
